/**
 * Componente contenedor de mapas para Forecast Metrics
 * Maneja el renderizado de mapas izquierdo y derecho con sus respectivos overlays
 */

import React from 'react';
import { Grid, Paper, Box } from '@mui/material';
import MapLeft from './maps/map_left.js';
import MapRight from './maps/map_right.js';
import Chip from '@mui/material/Chip';

/**
 * Contenedor de mapas para la vista Forecast Metrics
 * @param {Object} props - Propiedades del componente
 * @returns {JSX.Element} Componente de contenedor de mapas
 */
const ForecastMapContainer = ({
  prettyModelName,
  issueTime,
  cqlSevere,
  cqlFecha,
  mapLeftLayer,
  weekNumber,
  selectedTime,
  mapRightLayer,
  selectedModel,
  metricsName,
}) => {
  // Nombres legibles
  const LAYER_PRETTY = {
    'CNN_HISTORICAL:EU_025_live_demo': 'Convection (~28km)',
    'CNN_HISTORICAL:EU_01_live_demo': 'Convection (~9km)',
    'CNN_HISTORICAL:euro_1k_00625_15m': 'Convection (~6km)',
    // por si en algún momento usas las "live"
    'CNN:EU_025_live_demo': 'Convection',
    'CNN:EU_01_live_demo': 'Convection High Resolution',
  };

  const leftLabel = `AI ${LAYER_PRETTY[mapLeftLayer] ?? mapLeftLayer}`;
  const handleRiskClick = (risk) => {
    console.log("Riesgo seleccionado en el contenedor:", risk);
    // Aquí puedes añadir lógica extra si quieres que pase algo al hacer clic
  };
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: 'auto',
      overflowY: { xs: 'auto', md: 'hidden' },
      overflowX: 'hidden',
      minHeight: 0,
      
      paddingBottom:'80px'
    }}>
      <Grid
        container
        sx={{
          height: { xs: 'auto', md: '100%' },
          flex: { xs: '0 0 auto', md: 1 },

          minHeight: 0,
          margin: 0,
          '& > MuiGrid-Item': { paddingTop: 0, paddingBottom: 0 }
        }}
      >
        {/* Mapa Izquierdo - Forecast */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
           
            height: { xs: '50vh', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: 0
          }}
        >
          <Paper
            elevation={2}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 0,
              minHeight: 0,
              height: '100%'
            }}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                  textAlign: 'center',
                  pointerEvents: 'none', // no bloquea el mapa
                }}
              >
                <Chip
                  label={leftLabel}
                  sx={{
                    borderRadius: 2.5, // Ligeramente más pequeño
                    px: 1.5, // Menos relleno horizontal
                    height: 28, // Menos altura (antes 34)
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    fontSize: '0.75rem', // Tamaño de fuente más pequeño (antes 0.875rem)
                    backgroundColor: '#1e8e5f',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: '0 2px 6px rgba(30, 142, 95, 0.3)', // Box shadow ligeramente ajustado
                    transition: 'all .2s ease',
                    '&:hover': {
                      backgroundColor: '#176b49',
                      boxShadow: '0 3px 12px rgba(30, 142, 95, 0.4)', // Box shadow hover ajustado
                      transform: 'translateY(-1px)',
                    },
                    // Aseguramos que el padding interno de la etiqueta también se reduzca
                    '& .MuiChip-label': { px: 0.8 }, // Reducimos el padding interno del label (antes px: 1)
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <MapLeft
                  cqlSevere={cqlSevere}
                  cqlFecha={cqlFecha}
                  mapLeftLayer={mapLeftLayer}
                  weekNumber={weekNumber}
                  issueTime={issueTime}
                  selectedTime={selectedTime}
                  metricsName={metricsName}
                  handleRiskClick={handleRiskClick}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Mapa Derecho - Ground Truth */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            height: { xs: '50vh', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            
            
          }}
        >
          <Paper
            elevation={2}
            sx={{ position: 'relative', height: '100%', zIndex: 0, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 0,
                right: 0,
                zIndex: 1,
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              <Chip
                label="Ground truth"
                sx={{
                  borderRadius: 2.5, // Similar al verde (antes 2)
                  px: 1.5, // Similar al verde (antes 2.5)
                  height: 28, // Similar al verde (antes 34)
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'none',
                  fontSize: '0.75rem', // Similar al verde (antes 0.875rem)
                  backgroundColor: '#2080e8',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 2px 6px rgba(32, 128, 232, 0.3)', // Ajuste de box-shadow
                  transition: 'all .2s ease',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 3px 12px rgba(32, 128, 232, 0.4)', // Ajuste de box-shadow hover
                    transform: 'translateY(-1px)',
                  },
                  '& .MuiChip-label': { px: 0.8 }, // Similar al verde (antes 1)
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <MapRight
                cqlSevere={cqlSevere}
                cqlFecha={cqlFecha}
                mapRightLayer={mapRightLayer}
                weekNumber={weekNumber}
                issueTime={issueTime}
                selectedModel={selectedModel}
                isInteractive={true}
                metricsNames={metricsName}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ForecastMapContainer;
